/**
 * @fileOverview Everything on screen that holds edits not saved yet, and what happens when it would be
 * left: the leave question, the local drafts, the tab title mark and Ctrl+S.
 *
 * A surface (a record page, a report panel, an open catalogue row) registers here through
 * `useUnsavedChanges` (composables/useUnsavedChanges.ts), which does the tracking. Everything global
 * reads this registry, so no page wires up a guard of its own. See docs/unsaved-changes.md.
 */

// Nothing from the auth store is imported here: this module is loaded by App.vue, and the store loads
// the router, whose module body registers the guard below. Who is signed in is handed in instead.
import { computed, reactive, shallowReactive, shallowRef } from "vue"
import { START_LOCATION, type Router } from "vue-router"
import { IS_VIEW_HELP_EMBED } from "@/services/viewHelp/embed"

/** What the leave question needs of something it asks about. */
export interface LeaveQuestionEntry {
  /** What the question names it by: the record's icon and name. */
  label: () => { icon: string; name: string }
  isDirty: () => boolean
  /** Takes the latest keystroke into `isDirty` (and the draft) before anything is decided on it. */
  flush?: () => void
  /** Throws the edits away: tracked as saved from now on, and the draft is gone. */
  discard: () => void
  /**
   * `submit` answers whether the save went through (`Promise<boolean>`), so the question may offer
   * Speichern and carry on afterwards. A create page does not: its save may stop at a duplicate or
   * seat-limit dialog, and ends on the new record's page.
   */
  savesOnLeave: boolean
  /** What its own Speichern button does. */
  submit?: () => unknown
  /** Brings it back into view when the user chooses to keep editing (a minimised report panel). */
  reveal?: () => void
}

/** A surface on screen: a record page, a report panel, an open catalogue row. */
export interface UnsavedChangesEntry extends LeaveQuestionEntry {
  flush: () => void
  /** What its own Speichern button does. Ctrl+S calls it when there is something to save. */
  submit: () => unknown
  /** Where Ctrl+S goes first: a panel in front of the page, then an open row, then the page. */
  layer: "panel" | "row" | "page"
  /** Whether it is in front right now. A minimised panel is not. */
  isFront: () => boolean
}

const entries = shallowReactive(new Set<UnsavedChangesEntry>())

export function registerUnsavedChanges(entry: UnsavedChangesEntry) {
  entries.add(entry)
  return () => entries.delete(entry)
}

const dirtyEntries = () => [...entries].filter(entry => entry.isDirty())

/** Anything on screen with edits not saved yet. Reactive. */
export const hasUnsavedChanges = computed(() => dirtyEntries().length > 0)

/* -------------------------------------------------------------------------------------------------
 * The leave question
 * -----------------------------------------------------------------------------------------------*/

export type LeaveChoice = "save" | "discard" | "stay"

interface LeavePrompt {
  entries: LeaveQuestionEntry[]
  /** Speichern is offered only when every one of them can say whether its save went through. */
  canSave: boolean
  saving: boolean
  resolve: (_choice: LeaveChoice) => void
}

/** The question on screen, read by `LewUnsavedChangesDialog` (mounted once, in App.vue). */
export const leavePrompt = shallowRef<LeavePrompt | null>(null)

/** The question about the whole page, while it is on screen. */
let pendingDecision: Promise<boolean> | null = null

/**
 * Asks before `scope` (everything with changes, by default) is thrown away, and does what the user
 * chose. Resolves `true` when it may go: nothing was changed, the edits were saved, or discarded.
 *
 * A second call about the whole page while that question is open gets the same answer instead of a
 * second dialog: a click that navigates twice (a router-link inside a list item that pushes too) asks
 * once. Anything else asked while a question is open is declined: the one on screen comes first, and
 * the answer to "close this report" says nothing about the rest of the page.
 */
export function confirmLeave(scope?: LeaveQuestionEntry[]): Promise<boolean> {
  if (pendingDecision && !scope) return pendingDecision
  const candidates: LeaveQuestionEntry[] = scope ?? [...entries]
  candidates.forEach(entry => entry.flush?.())
  const dirty = candidates.filter(entry => entry.isDirty())
  if (dirty.length === 0) return Promise.resolve(true)
  if (leavePrompt.value) return Promise.resolve(false)
  // The Erklärung's frame has no dialog to ask with (App.vue), and its walkthrough closes the forms it
  // filled with sample data: nothing there is anybody's work.
  if (IS_VIEW_HELP_EMBED) {
    dirty.forEach(entry => entry.discard())
    return Promise.resolve(true)
  }

  const decision = new Promise<boolean>(resolve => {
    // Once answered, the question goes, unless another one has taken its place meanwhile (a save the
    // user stopped waiting for finishes after the next question is on screen).
    const settle = (result: boolean) => {
      if (leavePrompt.value === prompt) leavePrompt.value = null
      resolve(result)
    }
    const prompt = reactive<LeavePrompt>({
      entries: dirty,
      canSave: dirty.every(entry => entry.savesOnLeave && entry.submit),
      saving: false,
      resolve: async choice => {
        if (choice === "discard") {
          dirty.forEach(entry => entry.discard())
          settle(true)
        } else if (choice === "save") {
          prompt.saving = true
          // One after the other, so that a failed one keeps the rest from going out.
          for (const entry of dirty) {
            const saved = await Promise.resolve(entry.submit?.()).catch(() => false)
            if (saved !== true) {
              // The page shows why (the invalid tab, the error on the snackbar). Stay and let it.
              entry.reveal?.()
              settle(false)
              return
            }
          }
          settle(true)
        } else {
          // Also while a save is on its way: it finishes in the background, and the page stays.
          dirty[0].reveal?.()
          settle(false)
        }
      },
    })
    leavePrompt.value = prompt
  })
  if (scope) return decision
  pendingDecision = decision.finally(() => (pendingDecision = null))
  return pendingDecision
}

/* -------------------------------------------------------------------------------------------------
 * Drafts
 *
 * While a surface has changes, they are written to localStorage, so a crash, a closed laptop or an
 * expired session does not take them along. Keyed by workspace and person: the device may be a site
 * tablet several people sign in on.
 * -----------------------------------------------------------------------------------------------*/

export interface Draft<T = unknown> {
  /** When it was last written (ms). */
  savedAt: number
  /** The browser tab that wrote it: the same record may be open in two, and each keeps its own. */
  tab?: string
  /** The edits as they were on screen. */
  data: T
  /** The saved state they started from — what the restore tells the user's edits by. */
  base: T
  /** `lastModifiedAt` of that saved state, when the record has one. */
  version: string | null
}

export interface DraftKey {
  type: string
  id: string | number
}

/** The signed-in person's workspace and user id. Either is empty while nobody is signed in. */
export interface DraftOwner {
  tenant: string | null
  userId: string | null
}

const DRAFT_PREFIX = "lewara.draft."
/** A draft nobody came back to in two weeks is not coming back. */
const DRAFT_MAX_AGE = 14 * 24 * 60 * 60 * 1000

const draftsEnabled = !IS_VIEW_HELP_EMBED

const TAB_KEY = "lewara.draftTab"
/** This tab, for the drafts it writes. Kept in sessionStorage, so a reload is still the same tab. */
const TAB_ID = (() => {
  const fresh = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
  try {
    const known = sessionStorage.getItem(TAB_KEY)
    if (known) return known
    const id = fresh()
    sessionStorage.setItem(TAB_KEY, id)
    return id
  } catch {
    return fresh()
  }
})()

const ownerPrefix = ({ tenant, userId }: DraftOwner) => `${DRAFT_PREFIX}${tenant}.${userId}.`

/** The storage key, or null while nobody is signed in (or the person is not known yet). */
export function draftStorageKey(owner: DraftOwner, key: DraftKey): string | null {
  if (!draftsEnabled || !owner.tenant || !owner.userId) return null
  return `${ownerPrefix(owner)}${key.type}.${key.id}`
}

export function readDraft<T>(storageKey: string): Draft<T> | null {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return null
    const draft = JSON.parse(raw) as Draft<T>
    if (!draft || typeof draft.savedAt !== "number" || Date.now() - draft.savedAt > DRAFT_MAX_AGE) {
      localStorage.removeItem(storageKey)
      return null
    }
    return draft
  } catch {
    return null
  }
}

export function writeDraft(storageKey: string, draft: Draft) {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ ...draft, tab: TAB_ID }))
  } catch {
    // Full or blocked: the leave question still guards the edits, there is just no copy to come back to.
  }
}

export function removeDraft(storageKey: string) {
  try {
    localStorage.removeItem(storageKey)
  } catch {
    /* storage unavailable */
  }
}

/**
 * Removes the draft if this tab wrote it. Another tab with the same record open keeps its own: this
 * tab having nothing unsaved says nothing about that one.
 */
export function removeOwnDraft(storageKey: string) {
  const draft = readDraft(storageKey)
  if (draft && (!draft.tab || draft.tab === TAB_ID)) removeDraft(storageKey)
}

/** Every draft stored under `prefix`, the whole key included. */
function draftKeys(prefix = DRAFT_PREFIX): string[] {
  try {
    return Object.keys(localStorage).filter(key => key.startsWith(prefix))
  } catch {
    return []
  }
}

/**
 * Removes the signed-in person's drafts. Called on signing out, not when a session merely expires:
 * signing out is saying "done here" on a device someone else may pick up next, an expired session is
 * the case the drafts are kept for.
 */
export function clearOwnDrafts(owner: DraftOwner) {
  if (!owner.tenant || !owner.userId) return
  draftKeys(ownerPrefix(owner)).forEach(removeDraft)
}

/** Drops the drafts past their age, whoever they belong to. Once per page load. */
function pruneDrafts() {
  draftKeys().forEach(key => readDraft(key))
}

/* -------------------------------------------------------------------------------------------------
 * Guards
 * -----------------------------------------------------------------------------------------------*/

const sameQuery = (a: Record<string, unknown>, b: Record<string, unknown>) =>
  JSON.stringify(Object.entries(a).sort()) === JSON.stringify(Object.entries(b).sort())

/** A dialog is open: Ctrl+S is that dialog's business, not the page's behind it. */
const isDialogOpen = () => Boolean(document.querySelector(".v-dialog.v-overlay--active"))

function frontEntry(): UnsavedChangesEntry | undefined {
  const inFront = [...entries].filter(entry => entry.isFront())
  return (
    inFront.find(entry => entry.layer === "panel") ??
    inFront.find(entry => entry.layer === "row") ??
    inFront.find(entry => entry.layer === "page")
  )
}

function onKeydown(event: KeyboardEvent) {
  // `key` is missing on the keydown Chrome sends for an autofill.
  if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey || event.key?.toLowerCase() !== "s") return
  const entry = frontEntry()
  if (!entry) return
  // Also when nothing happens: the browser's "save page as" is never what someone in a form meant.
  event.preventDefault()
  // Held down, the keys repeat: one save is what was meant.
  if (event.repeat || isDialogOpen() || leavePrompt.value) return
  entry.flush()
  // Nothing to save is nothing to send: an unchanged record is not written again, and an empty create
  // form does not answer with its validation errors.
  if (entry.isDirty()) entry.submit()
}

function flushAll() {
  entries.forEach(entry => entry.flush())
}

/**
 * Registered before the update guard, which swaps a pending release in with a document load on the
 * next page change: that load must only ever happen once the page has been let go.
 *
 * @param isSignedIn whether the session still holds, read on each navigation
 */
export function initializeUnsavedChangesGuard(router: Router, isSignedIn: () => boolean) {
  // The Erklärung's frame is a picture of the product: its edits are sample data nobody keeps, and a
  // question inside it would stop the walkthrough from moving on.
  if (IS_VIEW_HELP_EMBED) return

  pruneDrafts()

  router.beforeEach((to, from) => {
    if (from === START_LOCATION) return true
    // A tab switch: the hash is this page's state, not a way out of it.
    if (to.path === from.path && sameQuery(to.query, from.query)) return true
    // The session is gone (the 401 bounce to Login): nothing can be saved any more, and asking would
    // keep the user on a page where every request fails. The draft keeps the edits for afterwards. A
    // question already on screen goes too: answered over the Login form, its Verwerfen would delete
    // the very draft this is counting on.
    if (!isSignedIn()) {
      leavePrompt.value?.resolve("stay")
      return true
    }
    return confirmLeave()
  })

  // Reload, closing the tab, typing another address. The browser asks in its own words; a page cannot
  // choose them any more.
  window.addEventListener("beforeunload", event => {
    flushAll()
    if (!hasUnsavedChanges.value) return
    event.preventDefault()
    event.returnValue = ""
  })
  // A tab put away may never come back (a phone killing it, a laptop running flat). The draft is
  // written a moment after each change; this writes the last one now.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushAll()
  })
  window.addEventListener("keydown", onKeydown)
}
